/**
 * Editable list: phone type + number per row (GET/PATCH borrowers[].phoneNumbers).
 * Legacy borrowers[].phone remains; primary summary uses derivePrimaryPhoneNumber.
 */
import { useFieldArray, useFormContext, Controller } from 'react-hook-form';
import { Box, Button, Grid, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { fmtPhone, formatPhoneInputValue } from '../../../utils/formatters';
import { BORROWER_PHONE_TYPES } from '../../../utils/borrowerPhones';

const MAX_PHONES = 10;

const PHONE_TYPE_OPTIONS = BORROWER_PHONE_TYPES.map((v) => ({ value: v, label: v }));

/** Read-only: typed list or legacy single phone */
export function BorrowerPhonesReadOnly({ borrower }) {
  const rows = (borrower.phoneNumbers || []).filter((p) => (p.number || '').trim());

  if (rows.length > 0) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Phone numbers</Typography>
        <StackRows rows={rows} />
      </Box>
    );
  }

  if (borrower.phone && String(borrower.phone).trim()) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Phone</Typography>
        <Typography variant="body2">{fmtPhone(borrower.phone)}</Typography>
      </Box>
    );
  }

  return <Typography variant="body2" color="text.secondary">—</Typography>;
}

function StackRows({ rows }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {rows.map((r, i) => (
        <Typography key={i} variant="body2">
          <Typography component="span" color="text.secondary" sx={{ mr: 1 }}>{r.phoneType || 'Phone'}:</Typography>
          {fmtPhone(r.number)}
        </Typography>
      ))}
    </Box>
  );
}

export default function BorrowerPhoneNumbersField({ namePath }) {
  const { control } = useFormContext();
  const base = `${namePath}.phoneNumbers`;
  const { fields, append, remove } = useFieldArray({ control, name: base });

  const canAdd = fields.length < MAX_PHONES;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Phone numbers</Typography>
      {fields.map((field, index) => (
        <Grid container spacing={1} key={field.id} alignItems="flex-start" sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name={`${base}.${index}.phoneType`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  value={f.value ?? 'Mobile'}
                  select
                  fullWidth
                  size="small"
                  label="Phone type"
                >
                  {PHONE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Controller
              name={`${base}.${index}.number`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  fullWidth
                  size="small"
                  label="Phone number"
                  value={formatPhoneInputValue(f.value)}
                  onChange={(e) => f.onChange(formatPhoneInputValue(e.target.value))}
                  onBlur={f.onBlur}
                  placeholder="(555) 123-4567"
                  inputMode="tel"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
            <IconButton size="small" aria-label="Remove phone" onClick={() => remove(index)} color="default">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => append({ phoneType: 'Mobile', number: '', sortOrder: fields.length })}
        disabled={!canAdd}
        sx={{ mt: 0.5 }}
      >
        Add phone number
      </Button>
      {fields.length >= MAX_PHONES && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Maximum {MAX_PHONES} numbers</Typography>
      )}
    </Box>
  );
}
